import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { useRestClient, RestSchemas } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

// Shadcn components
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Switch } from '@/components/shadcn/switch'
import { Textarea } from '@/components/shadcn/textarea'

import { toast } from 'sonner'
import { useCurrentApplication } from '../useCurrentApplication'

type MessagesEntityTypesExistingEntityType = RestSchemas['Messages.EntityTypes.ExistingEntityType']
type MessagesEntityTypesNewEntityType = RestSchemas['Messages.EntityTypes.NewEntityType']
type MessagesEntityTypesUpdateEntityType = RestSchemas['Messages.EntityTypes.UpdateEntityType']

// Form validation schema
const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name cannot exceed 100 characters'),
  description: z.string().max(200, 'Description cannot exceed 200 characters').optional(),
  applicationId: z.number(),
  isActive: z.boolean().optional(),
})

export type TEntityTypeForm = z.infer<typeof formSchema>

type AppEntityTypeFormV2Props = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedEntityType?: MessagesEntityTypesExistingEntityType
  // applicationId: number
}

export const AppEntityTypeFormV2 = (props: AppEntityTypeFormV2Props) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedEntityType
  const { applicationId } = useCurrentApplication()
  const {
    putEntityTypeApiUpdateEntityType,
    postEntityTypeApiCreateEntityType,
    getEntityTypeApiGetEntityTypesByApplicationId,
  } = useRestClient()

  const { mutate: updateEntityType, isPending: isUpdating } = putEntityTypeApiUpdateEntityType.useMutation(undefined, {
    onSuccess: () => {
      getEntityTypeApiGetEntityTypesByApplicationId.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Entity type successfully updated')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error in updating entity type')
    },
  })

  const { mutate: addEntityType, isPending: isAdding } = postEntityTypeApiCreateEntityType.useMutation(undefined, {
    onSuccess: () => {
      getEntityTypeApiGetEntityTypesByApplicationId.invalidateQueries({})
      if (props.sheetTrigger) {
        setSheetOpen(false)
      }
      toast.success('Entity type successfully added')
      props.onSubmit?.()
    },
    onError: () => {
      toast.error('Error in adding entity type')
    },
  })

  const { selectedEntityType: { id: selectedEntityTypeId } = {} } = props
  const submitHandler = useCallback(
    (data: TEntityTypeForm) => {
      if (edit && selectedEntityTypeId) {
        const updatedEntityType: MessagesEntityTypesUpdateEntityType = {
          id: selectedEntityTypeId,
          applicationId,
          name: data.name,
          description: data.description || '',
        }
        updateEntityType({ body: updatedEntityType })
      } else {
        const newEntityType: MessagesEntityTypesNewEntityType = {
          applicationId,
          name: data.name,
          description: data.description || '',
        }
        addEntityType({ body: newEntityType })
      }
    },
    [addEntityType, applicationId, edit, selectedEntityTypeId, updateEntityType]
  )

  const isLoading = isUpdating || isAdding

  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      wide
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (
  props: AppEntityTypeFormV2Props & {
    submitHandler: (data: TEntityTypeForm) => void
  }
) => {
  const selectedEntityType: Partial<MessagesEntityTypesExistingEntityType> = props.selectedEntityType ?? {}
  const { name, description } = selectedEntityType
  const edit = !!props.selectedEntityType
  const { applicationId } = useCurrentApplication()

  const defaultValues = useMemo(() => {
    return {
      name: name ?? '',
      description: description ?? '',
      applicationId,
      isActive: true, // Default to active for new entity types
    }
  }, [applicationId, name, description])

  const form = useForm<TEntityTypeForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(props.submitHandler)}>
          <div className="flex h-full flex-col">
            <div className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={4} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Active</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Enable this entity type for use in the application
                      </div>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="form-action-container">
              <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
            </div>
          </div>
        </form>
      </Form>
    </>
  )
}
